/*
 * In-place strength swap: treat the linked Regular / Extra Strength products like variants.
 *
 * The "Choose Your Strength" toggle (snippets/product-variants.liquid) links to the sibling product.
 * On the main PDP section, a click on a link marked [data-strength-swap] fetches the sibling's
 * main-product section through the Section Rendering API (keeping the shopper's supply option and
 * subscription plan) and swaps it in: gallery, title, price, Skio plan picker, buy buttons and
 * sticky bar all become the sibling's, so add to cart adds the sibling's real variant. The rest of
 * the page stays, like a variant change, and the URL is replaced with the sibling's so a refresh
 * or share lands on the right product.
 *
 * Anything unexpected (modifier-click, theme editor, sold-out or different-template sibling, fetch
 * or parse failure) falls back to the plain link, i.e. a normal page load.
 */
(() => {
  const SECTION_SUFFIX = '__main-product';
  let busy = false;

  const variantKey = (variant, strengthIndex) =>
    (variant && Array.isArray(variant.options) ? variant.options : [])
      .filter((_, index) => index !== strengthIndex)
      .join(' / ');

  const buildTargetUrl = (link, section) => {
    const target = new URL(link.href, window.location.origin);
    target.search = '';

    try {
      const map = JSON.parse(link.dataset.variantMap || '{}');
      const fieldset = link.closest('[data-own-strength-index]');
      const strengthIndex = parseInt(fieldset ? fieldset.dataset.ownStrengthIndex : '-1', 10);
      const selected = JSON.parse(section.querySelector('product-info [data-selected-variant]')?.textContent || 'null');
      const variantId = map[variantKey(selected, strengthIndex)];
      if (variantId) target.searchParams.set('variant', variantId);
    } catch (error) {
      // No variant: the sibling opens on its default variant.
    }

    // Keep the purchase type. Skio writes the chosen plan into the form's selling_plan input
    // (empty = one-time). The paired products share Skio selling plan groups.
    const planInput = section.querySelector('input[name="selling_plan"]');
    const plan = planInput ? planInput.value : new URLSearchParams(window.location.search).get('selling_plan');
    if (plan) target.searchParams.set('selling_plan', plan);

    return target;
  };

  const swap = async (link, section) => {
    const target = buildTargetUrl(link, section);
    const request = new URL(target);
    request.searchParams.set('section_id', section.id.replace(/^shopify-section-/, ''));

    busy = true;
    section.setAttribute('aria-busy', 'true');
    section.style.transition = 'opacity 150ms ease';
    section.style.opacity = '0.5';

    try {
      const response = await fetch(request.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.getElementById(section.id);
      if (!fresh || !fresh.querySelector('product-info')) throw new Error('sibling section not found');

      section.innerHTML = fresh.innerHTML;
      window.history.replaceState({ path: target.toString() }, '', target.toString());
      // The full SEO <title> (Yoast) is not part of the section; use the theme's fallback format
      // until the next full page load.
      if (link.dataset.documentTitle) document.title = link.dataset.documentTitle;
      document.dispatchEvent(new CustomEvent('strength:swapped', {
        detail: { url: target.toString(), sectionId: section.id }
      }));
    } catch (error) {
      console.warn('[strength-inplace-swap] falling back to page load', error);
      window.location.href = target.toString();
    } finally {
      section.style.opacity = '';
      section.removeAttribute('aria-busy');
      busy = false;
    }
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[data-strength-swap]');
    if (!link) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (window.Shopify && window.Shopify.designMode) return;

    const section = link.closest('[id^="shopify-section-"]');
    if (!section || !section.id.endsWith(SECTION_SUFFIX)) return;

    event.preventDefault();
    if (busy) return;
    swap(link, section);
  }, true);
})();
